export class OidcFlow {
  constructor(config) {
    // General
    this.businessContext = config.businessContext || null;
    this.authScheme = config.authScheme || this.getAuthScheme(this.businessContext);

    // OIDC
    this.clientId = config.clientId;
    this.oidcTokenStorageKey = 'oidcToken';
    this.accessTokenValidityMs = config.accessTokenValidityMs;
    // Browser authorization redirects must go to WEN, never to a token-request proxy.
    this.authUrl = `https://${config.wenEnvironment}/auth/oauth2/realms/root/realms/${config.realm}`;
    // DEV_LOCAL overrides fetch requests only, to avoid WEN's localhost CORS restriction.
    // Other environments omit the override and send token requests directly to WEN.
    this.tokenBaseUrl = config.tokenBaseUrl || this.authUrl;
    this.authRealm = config.realm;
    this.acrValues = config.acr_values;
    this.wenAuthorizationHeader = 'Basic ' + btoa(unescape(encodeURIComponent(`${config.clientId}:`)));

    this.setSessionId();
  }

  setSessionId() {
    let id = sessionStorage.getItem('cavors-session-id');
    if (!id) {
      id = this.createRandomUuid();
      sessionStorage.setItem('cavors-session-id', id);
      console.log(this.logPrefix('Auth') + 'Starting session with ID: ' + id + '.');
    }
  }

  start() {
    const tokenUrlInfo = this.tokenBaseUrl !== this.authUrl ? ` Token endpoint proxied via ${this.tokenBaseUrl}.` : '';
    console.log(this.logPrefix('Auth') + `Using realm '${this.authRealm}' with client '${this.clientId}' via ${this.authUrl}.${tokenUrlInfo}`);
    if (this.oidcIsSupported()) {
      this.startOrContinueOidcFlow();
    } else {
      this.onAuthCompleted();
    }

    if (this.getQueryParam('terminalServer')) {
      localStorage.setItem('terminalServer', 'true');
    }
  }

  startOrContinueOidcFlow() {
    console.log(this.logPrefix('Auth') + 'Starting or continuing OIDC flow.');
    this.discardIncompatibleCachedAlphaToken();
    if (this.getOidcTokenFromStorage()) {
      this.refreshAccessTokenIfExpired()
        .then(this.onAuthCompleted.bind(this))
        .catch(this.redirectToLoginPage.bind(this));
    } else if (this.authorizationCodeIsInUrl() && this.csrfTokenIsValid()) {
      this.requestAccessToken()
        .then(this.onAuthCompleted.bind(this))
        .catch(this.redirectToLoginPage.bind(this));
    } else {
      this.redirectToLoginPage();
    }
  }

  discardIncompatibleCachedAlphaToken() {
    const storedToken = this.getOidcTokenFromStorage();
    if (storedToken && this.authRealm === 'alpha' && JSON.parse(storedToken)?.client_id !== this.clientId) {
      console.info(this.logPrefix('Auth') + 'Discarding a cached token from a different OIDC client before Alpha login.');
      this.removeOidcTokenFromStorage();
      this.businessContext = null;
    }
  }

  onAuthCompleted() {
    if (this.oidcIsSupported()) {
      this.scheduleTimerForAccessTokenRefresh();
    }
    this.requestUserData()
      .then(this.handleUserData.bind(this))
      .catch(() => {
        // Do nothing
      });
  }

  oidcIsSupported() {
    return this.clientId.length > 0;
  }

  scheduleTimerForAccessTokenRefresh() {
    setInterval(() => {
      this.refreshAccessTokenIfExpired().catch();
    }, 30000);
  }

  dispatchOidcTokenRefreshedEvent() {
    window.dispatchEvent(new CustomEvent('oidcTokenRefreshed', {detail: this.getOidcTokenFromStorage()}));
  }

  handleUserData(user) {
    if (user.b2dUser && user.businessPartners.length === 0) {
      console.log(this.logPrefix('Auth') + 'User has no business relationships in S-Gate.');
      this.showError('Your user account seems to have no business relationships in S-Gate.');
    } else {
      window['bmwNamespace'] = {userData: user};
      window.dispatchEvent(new CustomEvent('userDataLoaded'));
    }
  }

  refreshAccessTokenIfExpired() {
    const tokenObj = JSON.parse(this.getOidcTokenFromStorage());

    if (!tokenObj) {
      console.error(this.logPrefix('Auth') + 'Cannot refresh OIDC access token, local storage is empty.');
      return Promise.reject();
    } else if (this.isAccessTokenExpired(tokenObj)) {
      console.log(this.logPrefix('Auth') + 'OIDC access token expired. Refreshing...');
      return this.refreshAccessToken(tokenObj);
    } else {
      console.debug(this.logPrefix('Auth') + 'OIDC access token is still fresh.');
      return Promise.resolve();
    }
  }

  isAccessTokenExpired(token) {
    return !token
      || !token.access_token_timestamp_ms
      || (new Date()).getTime() > token.access_token_refresh_timestamp_ms;
  }

  refreshAccessToken(tokenObj) {
    return new Promise((resolve, reject) => {
      const refreshRequest = this.buildRefreshOidcAccessTokenRequest(tokenObj.refresh_token);
      const url = this.tokenBaseUrl + '/access_token';
      const reqTimestampMs = (new Date()).getTime();

      fetch(url, refreshRequest)
        .then(response => {
          this.logResponseTime('POST', url, response.status, reqTimestampMs);
          if (response.status === 200) {
            response.json().then(newTokenObj => {
              this.handleSuccessfulAccessTokenRefresh(tokenObj, newTokenObj);
              resolve();
            });
          } else {
            this.handleFailedAccessTokenRefresh(tokenObj, response);
            reject();
          }
        })
        .catch(error => {
          console.error(this.logPrefix('Auth')
            + `Network error while retrieving OIDC access token from ${url}: ${error}.`);
          this.showError(`Authentication failed due to a network error.`);
          reject(error);
        });
    });
  }

  buildRefreshOidcAccessTokenRequest(refreshToken) {
    return {
      method: 'POST',
      headers: {
        'authorization': this.wenAuthorizationHeader,
        'content-type': 'application/x-www-form-urlencoded'
      },
      body: `grant_type=refresh_token&refresh_token=${refreshToken}&redirect_uri=${this.getRedirectUri()}`
    };
  }

  handleSuccessfulAccessTokenRefresh(tokenObj, newTokenObj) {
    const expiredAccessToken = tokenObj.access_token;
    tokenObj.access_token = newTokenObj.access_token;
    tokenObj.access_token_timestamp_ms = (new Date()).getTime();
    tokenObj.access_token_refresh_timestamp_ms = tokenObj.access_token_timestamp_ms + this.accessTokenValidityMs;
    // The id_token is guaranteed on the initial login (authorization_code grant with the openid
    // scope) but NOT on a refresh: WEN's refresh_token grant may omit it. It is required as the
    // id_token_hint for the WEN end-session (logout) request, so only overwrite the stored
    // id_token when the refresh response actually returns one; otherwise keep the existing one.
    if (newTokenObj.id_token) {
      tokenObj.id_token = newTokenObj.id_token;
    }
    this.putOidcTokenInStorage(tokenObj);
    this.dispatchOidcTokenRefreshedEvent();
    this.revokeOidcAccessToken(expiredAccessToken, 2500);
    console.log(this.logPrefix('Auth') + 'OIDC access token refresh successful.');
  }

  handleFailedAccessTokenRefresh(tokenObj, response) {
    response.json().then(errorObject => {
      console.error(this.logPrefix('Auth')
        + `Failed to refresh OIDC access token: HTTP ${response.status} ${response.statusText}.`, errorObject);
    }).catch(() => {
      console.error(this.logPrefix('Auth')
        + `Failed to refresh OIDC access token: HTTP ${response.status} ${response.statusText}.`);
    });
    if (response.status === 400 || response.status === 401) {
      this.removeOidcTokenFromStorage();
      this.revokeOidcAccessToken(tokenObj.access_token);
      this.redirectToLoginPage();
    } else {
      this.showError('Authentication failed due to an unknown application error.');
    }
  }

  authorizationCodeIsInUrl() {
    if (this.getQueryParam('error')) {
      throw new Error(this.getQueryParam('error') + ' - ' + this.getQueryParam('error_description'));
    }
    return this.getQueryParam('code') && this.getQueryParam('iss') && this.getQueryParam('client_id');
  }

  redirectToLoginPage() {
    console.log(this.logPrefix('Auth') + 'Redirecting to login page.');
    this.windowLocationReplace(this.getLoginPageUrl());
  }

  windowLocationReplace(url) {
    window.location.replace(url); // location.replace is readonly so spyOn can't replace it
  }

  getLoginPageUrl() {
    const url = new URL(this.authUrl + '/authorize');
    url.searchParams.set('client_id', this.clientId);
    url.searchParams.set('state', JSON.stringify(this.getApplicationState()));
    url.searchParams.set('scope', this.getScopes());
    url.searchParams.set('redirect_uri', this.getRedirectUri());
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('nonce', '');
    if (this.acrValues) {
      url.searchParams.set('acr_values', this.acrValues);
    }
    return url.href;
  }

  getScopes() {
    return 'openid profile email phone address bmwids organization b2xroles b2d';
  }

  requestAccessToken() {
    return new Promise((resolve, reject) => {
      const headers = {};
      headers['content-type'] = 'application/x-www-form-urlencoded';
      headers['authorization'] = this.wenAuthorizationHeader;
      const body = `grant_type=authorization_code&code=${this.getQueryParam('code')}&redirect_uri=${this.getRedirectUri()}`;
      const url = this.tokenBaseUrl + '/access_token';

      const reqTimestampMs = (new Date()).getTime();
      fetch(
        url,
        {
          method: 'POST',
          headers: headers,
          body: body
        }
      )
        .then(response => {
          this.logResponseTime('POST', url, response.status, reqTimestampMs);
          if (response.status === 200) {
            response.json().then(oidcTokenObject => {
              oidcTokenObject.access_token_timestamp_ms = (new Date()).getTime();
              oidcTokenObject.refresh_token_timestamp_ms = oidcTokenObject.access_token_timestamp_ms;
              oidcTokenObject.access_token_refresh_timestamp_ms =
                oidcTokenObject.access_token_timestamp_ms + this.accessTokenValidityMs;
              if (this.businessContext) {
                oidcTokenObject.business_context = this.businessContext;
              }
              oidcTokenObject.client_id = this.clientId;
              this.putOidcTokenInStorage(oidcTokenObject);
              this.dispatchOidcTokenRefreshedEvent();
              this.restoreDeepLink();
              resolve();
            });
          } else {
            console.log(this.logPrefix('Auth')
              + `Failed to retrieve OIDC access token: HTTP ${response.status} ${response.statusText}`);
            reject(response.statusText);
          }
        })
        .catch(error => {
          console.log(this.logPrefix('Auth')
            + `Network error while retrieving OIDC access token from ${url}: ${error}.`);
          reject(error);
        });

    });
  }

  requestUserData() {
    return new Promise((resolve, reject) => {
      const url = '/api/user/v1';
      const reqTimestampMs = (new Date()).getTime();
      fetch(
        url,
        {
          method: 'GET',
          headers: this.getHeaders(false)
        }
      ).then(response => {
        this.logResponseTime('GET', url, response.status, reqTimestampMs);
        if (response.status === 200) {
          response.json().then(json => {
            try {
              this.applyBusinessContextFromUserData(json.data);
            } catch (error) {
              this.showError(error.message);
              reject(error.message);
              return;
            }
            resolve(json.data);
          });
        } else {
          this.handleUserDataErrorResponse(response);
          reject(`Failed to retrieve user data from ${url}: HTTP ${response.status}.`);
        }
      }).catch(error => {
        reject(`Network error while retrieving user data from ${url}: ${error}.`);
      });
    });
  }

  getHeaders(includeBusinessContext = true) {
    const headers = new Headers();
    const oidcToken = JSON.parse(this.getOidcTokenFromStorage()); // Potentially null
    const accessToken = oidcToken ? oidcToken.access_token : null;
    const businessContext = this.getCurrentBusinessContext();
    headers.set('AuthRealm', this.authRealm);
    headers.set('AuthScheme', this.getAuthScheme(businessContext));
    if (includeBusinessContext && businessContext) {
      headers.set('BusinessContext', businessContext);
    }
    if (accessToken) {
      headers.set('access_token', accessToken);
      headers.set('authorization', 'Bearer ' + accessToken);
      headers.set('AuthType', 'WEN_TOKEN');
    }
    headers.set('cavors-request-id', this.createRandomUuid());
    const id = sessionStorage.getItem('cavors-session-id');
    if (!id) {
      console.warn(this.logPrefix('Auth') + 'CaVORS session id not set before logging.');
      this.setSessionId();
    }
    headers.set('cavors-session-id', sessionStorage.getItem('cavors-session-id') || '');
    return headers;
  }

  handleUserDataErrorResponse(response) {
    const businessContext = this.getCurrentBusinessContext() || 'unknown';
    const message = `Failed to retrieve user data for business context ${businessContext}: HTTP ${response.status} ${response.statusText}.`;
    console.log(this.logPrefix('Auth') + message);
    if (response.status === 403) {
      response.json().then(responseObject => {
        const mismatchError = responseObject.errors
          && responseObject.errors.find(e => e.code === 'AWP_USER_BUSINESS_CONTEXT_MISMATCH');
        if (mismatchError) {
          this.showError(mismatchError.message);
        } else {
          this.showError('To request access to the AWP in ' + businessContext + ', please contact your S-Gate administrator.');
        }
      }).catch(() => {
        this.showError('To request access to the AWP in ' + businessContext + ', please contact your S-Gate administrator.');
      });
    } else if (response.status < 200 || response.status >= 300) {
      // Check if we got a structured AWP error response with more specific information
      response.json().then(responseObject => {
        if (responseObject.message) {
          console.log(this.logPrefix('Auth') + 'Response: ', responseObject); // Should be { message, requestUrl,
                                                                              // statusCode }
          this.showError(responseObject.message);
        } else {
          this.showError(message);
        }
      }).catch(() => {
        this.showError(message);
      });
    }
  }

  getOidcTokenFromStorage() {
    const token = this.getLocalStorage().getItem(this.oidcTokenStorageKey);
    return token || this.getOidcTokenFromLegacyStorage();
  }

  // TODO(ISPI-484050 follow-up): Temporary workaround for clients still running a stale,
  // browser/CDN-cached copy of this script from before the OIDC token storage key was unified.
  // Such stale scripts store the token under the old per-business-context key
  // (e.g. 'oidcToken_B2D') instead of the current static 'oidcToken' key, so newer code -
  // running from the always-fresh, content-hashed Angular bundle - can't find it. Falls back to
  // the legacy key, migrates the token to the new key, and logs so occurrences can be traced.
  // Remove once telemetry confirms no more legacy-keyed tokens occur in production.
  getOidcTokenFromLegacyStorage() {
    if (!this.businessContext) {
      return null;
    }
    const legacyKey = 'oidcToken_' + this.businessContext;
    const legacyToken = this.getLocalStorage().getItem(legacyKey);
    if (legacyToken) {
      console.warn(this.logPrefix('Auth')
        + `Found OIDC token under legacy storage key '${legacyKey}' instead of '${this.oidcTokenStorageKey}'. `
        + 'This indicates a stale cached OidcFlow.js was used during login. Migrating token to the new key.');
      this.putItemInStorage(this.oidcTokenStorageKey, JSON.parse(legacyToken));
      this.getLocalStorage().removeItem(legacyKey);
    }
    return legacyToken;
  }

  putOidcTokenInStorage(token) {
    const tokenObject = typeof token === 'string' ? JSON.parse(token) : token;
    if (tokenObject.business_context) {
      this.businessContext = tokenObject.business_context;
    }
    this.putItemInStorage(this.oidcTokenStorageKey, tokenObject);
  }

  putItemInStorage(key, item) {
    this.getLocalStorage().setItem(key, JSON.stringify(item));
  }

  removeOidcTokenFromStorage() {
    this.getLocalStorage().removeItem(this.oidcTokenStorageKey);
  }

  restoreDeepLink() {
    const stateRaw = this.getQueryParam('state');
    if (stateRaw) {
      try {
        const stateObj = JSON.parse(stateRaw);
        if (stateObj.url) {
          console.log(this.logPrefix('Auth') + `OIDC flow complete. Restoring application state '${stateObj.url}'.`);
          history.replaceState(null, '', stateObj.url);
          return;
        }
      } catch {
        throw new Error('OIDC state parameter could not be parsed.');
      }
    }
    console.log(this.logPrefix('Auth') + 'OIDC flow complete. No application state to restore.');
  }

  showError(msg) {
    let splashScreenMessage = document.getElementById('splash-screen-message');
    if (splashScreenMessage) {
      splashScreenMessage.innerText = msg;
      document.getElementById('splash-screen-loader').hidden = true;
      splashScreenMessage.hidden = false;
    }
  }

  getQueryParam(name) {
    return (new URL(this.getHref())).searchParams.get(name);
  }

  getHostname() {
    return window.location.hostname; // Allows for easier mocking in unit tests
  }

  getHref() {
    return window.location.href; // Allows for easier mocking in unit tests
  }

  getRedirectUri() {
    return this.getHostname() === 'localhost' ? 'http://localhost:4200' : `https://${this.getHostname()}`;
  }

  getApplicationState() {
    // Application state contains a cross-site request forgery (CSRF) token and the current navigation.
    // The CSRF token ensures the response belongs to this request, see
    // https://developers.google.com/identity/openid-connect/openid-connect?hl=en#createxsrftoken
    const csrfToken = this.createRandomUuid() + this.createRandomUuid();
    this.getLocalStorage().setItem('csrfToken', csrfToken);
    return {
      csrfToken: csrfToken,
      url: window.location.pathname + window.location.search
    };
  }

  createRandomUuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (this.getCryptoRandom() * 16) | 0; // eslint-disable-line no-bitwise
      const v = c === 'x' ? r : (r & 0x3) | 0x8; // eslint-disable-line no-bitwise
      return v.toString(16);
    });
  }

  getCryptoRandom() {
    return window.crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32;
  }

  getLocalStorage() {
    return window.sessionStorage;
  }

  getStoredCsrfToken() {
    return this.getLocalStorage().getItem('csrfToken');
  }

  removeStoredCsrfToken() {
    this.getLocalStorage().removeItem('csrfToken');
  }

  logResponseTime(reqMethod, reqUrl, resStatus, reqTimestampMs) {
    // Preflight request responses have a status of undefined and a duration of 1-5ms, so those need to be filtered out
    if (resStatus) {
      const resTimestampMs = (new Date()).getTime();
      const method = ('[' + reqMethod + ']').padStart(9);
      const url = '[' + reqUrl + ']';
      const status = '[' + resStatus + ']';
      const durationMs = '[' + (resTimestampMs - reqTimestampMs) + ']';
      console.log(this.logPrefix('Network') + `[Http-Res] ${method} ${url} ${status} in ${durationMs}ms`);
    }
  }

  revokeOidcAccessToken(accessToken, gracePeriodMs = 0) {
    const revokeRequest = this.buildRevokeOidcAccessTokenRequest(accessToken);
    const revokeUrl = this.tokenBaseUrl + '/token/revoke';
    // Revoke the old token after a grace period to let ongoing requests finish
    setTimeout(() => this.sendRevokeOidcAccessTokenRequest(revokeUrl, revokeRequest), gracePeriodMs);
  }

  buildRevokeOidcAccessTokenRequest(accessToken) {
    return {
      method: 'POST',
      headers: {
        'authorization': this.wenAuthorizationHeader,
        'content-type': 'application/x-www-form-urlencoded'
      },
      body: `client_id=${this.clientId}&token=${accessToken}`
    };
  }

  sendRevokeOidcAccessTokenRequest(revokeUrl, revokeRequest) {
    fetch(revokeUrl, revokeRequest)
      .then(() => console.log(this.logPrefix('Auth') + 'Successfully revoked access token.'))
      .catch(() => console.log(this.logPrefix('Auth') + 'Failed to revoke access token.'));
  }

  logPrefix(component) {
    return (new Date()).toISOString() + ' [' + component + '] ';
  }

  csrfTokenIsValid() {
    const returnedStateRaw = this.getQueryParam('state');
    let returnedCsrfToken;
    try {
      const stateObj = JSON.parse(returnedStateRaw);
      returnedCsrfToken = stateObj.csrfToken;
    } catch {
      throw new Error('OIDC state parameter from URL could not be parsed.');
    }
    const storedCsrfToken = this.getStoredCsrfToken();
    if (!returnedCsrfToken || !storedCsrfToken || returnedCsrfToken !== storedCsrfToken) {
      console.warn('OIDC csrfToken mismatch! Possible CSRF/XSRF attack.');
      return false;
    } else {
      this.removeStoredCsrfToken();
      return true;
    }
  }

  getAuthScheme(businessContext) {
    return (businessContext || this.authScheme || 'B2D').substring(0, 3);
  }

  getCurrentBusinessContext() {
    return window.bmwNamespace?.userData?.businessContext || this.businessContext;
  }

  applyBusinessContextFromUserData(userData) {
    const businessContext = userData && userData.businessContext;
    if (!businessContext) {
      return;
    }

    const tokenJson = this.getLocalStorage().getItem(this.oidcTokenStorageKey);
    this.businessContext = businessContext;

    if (tokenJson) {
      const oidcTokenObject = JSON.parse(tokenJson);
      oidcTokenObject.business_context = businessContext;
      this.getLocalStorage().setItem(this.oidcTokenStorageKey, JSON.stringify(oidcTokenObject));
      this.dispatchOidcTokenRefreshedEvent();
    }
  }
}
